import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Button, ButtonGroup, Dropdown, Form, InputGroup } from "react-bootstrap";
import ConfirmModal from "../ConfirmModal/ConfirmModal";
import styles from "./NotepadTool.module.css";

const generateStorageKey = (notepadId) => `teacher_toolbar_notepad_${notepadId}`;

const createDefaultSection = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  name: "Bölmə 1",
  content: "",
  updatedAt: Date.now(),
});

const NotepadTool = React.forwardRef(({ notepadId, onClose }, ref) => {
  const storageKey = useRef(
    generateStorageKey(notepadId || `notepad_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`)
  ).current;

  const editorRef = useRef(null);
  const savedRangeRef = useRef(null);
  const previousSectionIdRef = useRef(null);
  const activeSectionIdRef = useRef(null);
  const sectionsRef = useRef([]);

  const [sections, setSections] = useState(() => [createDefaultSection()]);
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [editingSectionName, setEditingSectionName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchMatches, setSearchMatches] = useState(0);
  const [textColor, setTextColor] = useState("#111827");
  const [highlightColor, setHighlightColor] = useState(null);
  const [fontSize, setFontSize] = useState("14");
  const [fontFamily, setFontFamily] = useState("Arial");
  const [deleteConfirmShow, setDeleteConfirmShow] = useState(false);
  const [deleteSectionId, setDeleteSectionId] = useState(null);

  // Ref-ləri hər render-də yenilə
  useEffect(() => { sectionsRef.current = sections; }, [sections]);
  useEffect(() => { activeSectionIdRef.current = activeSectionId; }, [activeSectionId]);


  const saveToLocalStorage = useCallback((sectionsData, activeId) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify({ sections: sectionsData, activeSectionId: activeId }));
    } catch (e) {
      console.error("localStorage xətası:", e);
    }
  }, [storageKey]);

  const loadNotes = useCallback(() => {
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      const def = createDefaultSection();
      setSections([def]);
      setActiveSectionId(def.id);
      setIsLoaded(true);
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      const loaded = Array.isArray(parsed?.sections) && parsed.sections.length > 0
        ? parsed.sections : [createDefaultSection()];
      setSections(loaded);
      setActiveSectionId(parsed?.activeSectionId || loaded[0].id);
    } catch {
      const def = createDefaultSection();
      setSections([def]);
      setActiveSectionId(def.id);
    }
    setIsLoaded(true);
  }, [storageKey]);

  const clearSearchHighlights = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.querySelectorAll("mark[data-search='1']").forEach((node) => {
      const parent = node.parentNode;
      if (!parent) return;
      parent.replaceChild(document.createTextNode(node.textContent || ""), node);
      parent.normalize();
    });
  }, []);

  const getEditorHtml = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return "";
    const clone = editor.cloneNode(true);
    clone.querySelectorAll("mark[data-search='1']").forEach((node) => {
      const parent = node.parentNode;
      if (!parent) return;
      parent.replaceChild(document.createTextNode(node.textContent || ""), node);
      parent.normalize();
    });
    return clone.innerHTML.replace(/\u200B/g, "").replace(/\u00A0/g, " ");
  }, []);

  const syncEditorToState = useCallback(() => {
    if (!activeSectionId) return;
    const cleanHtml = getEditorHtml();
    setSections((prev) => {
      const updated = prev.map((s) =>
        s.id === activeSectionId ? { ...s, content: cleanHtml, updatedAt: Date.now() } : s
      );
      saveToLocalStorage(updated, activeSectionId);
      return updated;
    });
  }, [activeSectionId, getEditorHtml, saveToLocalStorage]);

  useImperativeHandle(ref, () => ({ save: syncEditorToState }), [syncEditorToState]);

  const applyCommand = useCallback((command, value = null) => {
    const editor = editorRef.current;
    if (!editor) return;
    // Use the live selection only when it is non-collapsed AND inside the editor.
    // A collapsed in-editor range means the actual text selection was lost — restore savedRange instead.
    const currentSel = window.getSelection();
    const currentRange = currentSel?.rangeCount ? currentSel.getRangeAt(0) : null;
    const hasLiveSelection =
      currentRange &&
      !currentRange.collapsed &&
      editor.contains(currentRange.commonAncestorContainer);
    editor.focus();
    if (!hasLiveSelection && savedRangeRef.current) {
      try {
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(savedRangeRef.current.cloneRange());
      } catch (e) { /* ignore */ }
    }
    document.execCommand(command, false, value);
    syncEditorToState();
  }, [syncEditorToState]);

  const applyFontFamily = useCallback((fontName) => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    if (savedRangeRef.current) {
      try {
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(savedRangeRef.current.cloneRange());
      } catch (e) {}
    }
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount || sel.isCollapsed) return;
    const range = sel.getRangeAt(0);
    const contents = range.extractContents();
    const span = document.createElement("span");
    span.style.fontFamily = fontName;
    span.appendChild(contents);
    range.insertNode(span);
    try {
      const after = document.createRange();
      after.setStartAfter(span);
      after.collapse(true);
      sel.removeAllRanges();
      sel.addRange(after);
      savedRangeRef.current = after.cloneRange();
    } catch (e) {}
    syncEditorToState();
  }, [syncEditorToState]);

  const saveCurrentSelection = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return;
    const range = selection.getRangeAt(0);
    if (editorRef.current && editorRef.current.contains(range.commonAncestorContainer)) {
      savedRangeRef.current = range.cloneRange();
    }
  }, []);


  const moveCaretOutsideHighlight = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return;
    const range = selection.getRangeAt(0);
    const startNode = range.startContainer;
    const parentElement = startNode?.nodeType === Node.ELEMENT_NODE ? startNode : startNode?.parentElement;
    const highlightNode = parentElement?.closest?.("mark[data-highlight='1']");
    if (!highlightNode) return;
    const nextRange = document.createRange();
    nextRange.setStartAfter(highlightNode);
    nextRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(nextRange);
    savedRangeRef.current = nextRange.cloneRange();
  }, []);

  const getHighlightNodeFromSelection = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return null;
    const range = selection.getRangeAt(0);
    const startNode = range.startContainer;
    const parentElement = startNode?.nodeType === Node.ELEMENT_NODE ? startNode : startNode?.parentElement;
    return parentElement?.closest?.("mark[data-highlight='1']") || null;
  }, []);

  const handleEditorBeforeInput = useCallback((event) => {
    const highlightNode = getHighlightNodeFromSelection();
    if (!highlightNode) return;
    if (!event.inputType || !event.inputType.startsWith("insert")) return;
    const insertedText = event.data || "";
    if (/^\s+$/.test(insertedText)) return;
    event.preventDefault();
    const selection = window.getSelection();
    const nextRange = document.createRange();
    nextRange.setStartAfter(highlightNode);
    nextRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(nextRange);
    const textNode = document.createTextNode(insertedText);
    nextRange.insertNode(textNode);
    const caretRange = document.createRange();
    caretRange.setStart(textNode, textNode.length);
    caretRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(caretRange);
    savedRangeRef.current = caretRange.cloneRange();
    syncEditorToState();
  }, [getHighlightNodeFromSelection, syncEditorToState]);

  const handleEditorInput = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) { syncEditorToState(); return; }
    const range = selection.getRangeAt(0);
    const node = range.startContainer;
    const offset = range.startOffset;
    moveCaretOutsideHighlight();
    if (searchMatches > 0) { clearSearchHighlights(); setSearchMatches(0); }
    try {
      if (node && node.parentNode && editorRef.current?.contains(node)) {
        const newRange = document.createRange();
        newRange.setStart(node, Math.min(offset, node.length || 0));
        newRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(newRange);
        savedRangeRef.current = newRange.cloneRange();
      }
    } catch (e) { /* ignore */ }
    syncEditorToState();
  }, [moveCaretOutsideHighlight, searchMatches, clearSearchHighlights, syncEditorToState]);

  const handleEditorKeyDown = useCallback((event) => {
    if (["ArrowLeft","ArrowRight","ArrowUp","ArrowDown"].includes(event.key)) return;
    moveCaretOutsideHighlight();
  }, [moveCaretOutsideHighlight]);


  const buildHighlightedFragment = useCallback((sourceNode, color) => {
    const fragment = document.createDocumentFragment();
    let lastNode = null;
    const processNode = (node, target) => {
      if (node.nodeType === Node.TEXT_NODE) {
        if (!node.nodeValue) return;
        const mark = document.createElement("mark");
        mark.setAttribute("data-highlight", "1");
        mark.className = styles.highlightMark;
        mark.style.backgroundColor = color;
        mark.style.color = "#111827";
        mark.style.borderRadius = "2px";
        mark.textContent = node.nodeValue;
        target.appendChild(mark);
        lastNode = mark;
        return;
      }
      if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.tagName === "MARK" && node.getAttribute("data-highlight") === "1") {
          Array.from(node.childNodes).forEach((child) => processNode(child, target));
          return;
        }
        const clone = node.cloneNode(false);
        Array.from(node.childNodes).forEach((child) => processNode(child, clone));
        target.appendChild(clone);
        lastNode = clone;
      }
    };
    Array.from(sourceNode.childNodes).forEach((child) => processNode(child, fragment));
    return { fragment, lastNode };
  }, []);

  const restoreSelectionFromRef = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return null;
    editor.focus();
    const saved = savedRangeRef.current;
    if (!saved) return null;
    try {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(saved.cloneRange());
      return sel.rangeCount ? sel.getRangeAt(0) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const applyHighlight = useCallback((color) => {
    const range = restoreSelectionFromRef();
    if (!range || range.collapsed) return;
    const contents = range.extractContents();
    const tmpDiv = document.createElement("div");
    tmpDiv.appendChild(contents);
    tmpDiv.querySelectorAll("mark[data-highlight='1']").forEach((m) => {
      const p = m.parentNode; if (!p) return;
      while (m.firstChild) p.insertBefore(m.firstChild, m); p.removeChild(m);
    });
    const srcFragment = document.createDocumentFragment();
    while (tmpDiv.firstChild) srcFragment.appendChild(tmpDiv.firstChild);
    const { fragment: highlighted, lastNode } = buildHighlightedFragment(srcFragment, color);
    const lastInserted = lastNode;
    range.insertNode(highlighted);
    if (lastInserted && lastInserted.parentNode) {
      try {
        const after = document.createRange();
        after.setStartAfter(lastInserted);
        after.collapse(true);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(after);
        savedRangeRef.current = after.cloneRange();
      } catch (e) {}
    }
    syncEditorToState();
  }, [buildHighlightedFragment, restoreSelectionFromRef, syncEditorToState]);

  const removeHighlight = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    // Restore selection
    if (savedRangeRef.current) {
      try {
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(savedRangeRef.current.cloneRange());
      } catch (e) {}
    }
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount || sel.isCollapsed) return;
    const range = sel.getRangeAt(0);
    // Find all highlight marks that intersect the selection and unwrap them
    const marks = Array.from(editor.querySelectorAll("mark[data-highlight='1']"))
      .filter((m) => range.intersectsNode(m));
    if (!marks.length) return;
    marks.forEach((mark) => {
      const parent = mark.parentNode;
      if (!parent) return;
      while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
      parent.removeChild(mark);
    });
    editor.normalize();
    // Collapse cursor to end of selection
    try {
      sel.collapseToEnd();
      if (sel.rangeCount) savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    } catch (e) {}
    syncEditorToState();
  }, [syncEditorToState]);

  const searchNote = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    clearSearchHighlights();
    const needle = searchTerm.trim();
    if (!needle) { setSearchMatches(0); return; }
    const nodes = [];
    const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      const current = walker.currentNode;
      if (!current.nodeValue?.trim()) continue;
      const pe = current.parentElement;
      if ((pe?.tagName === "MARK" && pe.getAttribute("data-search") === "1") || pe?.tagName === "SCRIPT" || pe?.tagName === "STYLE") continue;
      nodes.push(current);
    }
    let matches = 0;
    const termLower = needle.toLowerCase();
    nodes.forEach((textNode) => {
      const text = textNode.nodeValue || "";
      const textLower = text.toLowerCase();
      let index = 0; let hasMatch = false;
      const fragment = document.createDocumentFragment();
      while (index < text.length) {
        const foundAt = textLower.indexOf(termLower, index);
        if (foundAt === -1) { fragment.appendChild(document.createTextNode(text.slice(index))); break; }
        hasMatch = true;
        if (foundAt > index) fragment.appendChild(document.createTextNode(text.slice(index, foundAt)));
        const mark = document.createElement("mark");
        mark.setAttribute("data-search", "1");
        mark.className = styles.searchMark;
        mark.textContent = text.slice(foundAt, foundAt + needle.length);
        fragment.appendChild(mark);
        matches += 1;
        index = foundAt + needle.length;
      }
      if (hasMatch && textNode.parentNode) textNode.parentNode.replaceChild(fragment, textNode);
    });
    setSearchMatches(matches);
  }, [searchTerm, clearSearchHighlights]);

  const addSection = useCallback(() => {
    syncEditorToState();
    const nextSection = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      name: `Bölmə ${sections.length + 1}`,
      content: "",
      updatedAt: Date.now(),
    };
    setSections((prev) => {
      const updated = [...prev, nextSection];
      saveToLocalStorage(updated, nextSection.id);
      return updated;
    });
    setActiveSectionId(nextSection.id);
  }, [sections, syncEditorToState, saveToLocalStorage]);

  const startRenameSection = useCallback((section) => {
    setEditingSectionId(section.id);
    setEditingSectionName(section.name || "");
  }, []);

  const commitRenameSection = useCallback((sectionId) => {
    const nextName = editingSectionName.trim() || "Adsız bölmə";
    setSections((prev) => {
      const updated = prev.map((s) => s.id === sectionId ? { ...s, name: nextName, updatedAt: Date.now() } : s);
      saveToLocalStorage(updated, activeSectionId);
      return updated;
    });
    setEditingSectionId(null);
    setEditingSectionName("");
  }, [editingSectionName, activeSectionId, saveToLocalStorage]);

  const cancelRenameSection = useCallback(() => {
    setEditingSectionId(null);
    setEditingSectionName("");
  }, []);

  const deleteSection = useCallback((sectionId) => {
    const remaining = sections.filter((s) => s.id !== sectionId);
    if (remaining.length === 0) return;
    const nextActiveId = activeSectionId === sectionId ? remaining[remaining.length - 1].id : activeSectionId;
    setSections(remaining);
    setActiveSectionId(nextActiveId);
    saveToLocalStorage(remaining, nextActiveId);
  }, [sections, activeSectionId, saveToLocalStorage]);

  const requestDeleteSection = useCallback((sectionId) => {
    setDeleteSectionId(sectionId);
    setDeleteConfirmShow(true);
  }, []);

  const cancelDeleteSection = useCallback(() => {
    setDeleteConfirmShow(false);
    setDeleteSectionId(null);
  }, []);

  const confirmDeleteSection = useCallback(() => {
    if (!deleteSectionId) return;
    deleteSection(deleteSectionId);
    cancelDeleteSection();
  }, [cancelDeleteSection, deleteSection, deleteSectionId]);

  const extractTextFromHtml = useCallback((html) => {
    const container = document.createElement("div");
    container.innerHTML = html.replace(/​/g, "").replace(/ /g, " ");
    return (container.textContent || "").replace(/​/g, "").trim();
  }, []);

  const exportNotes = useCallback((type) => {
    if (type === "txt") {
      const combinedText = sections
        .map((s) => `=== ${s.name} ===\n${extractTextFromHtml(s.content)}`)
        .join("\n\n");
      const blob = new Blob([combinedText], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.download = "notlar.txt"; link.click();
      URL.revokeObjectURL(url);
      return;
    }
    if (type === "pdf") {
      const htmlBody = sections
        .map((s) => `<h3>${s.name}</h3><div>${s.content.replace(/​/g, "").replace(/ /g, " ")}</div>`)
        .join('<div class="sep"></div>');
      const win = window.open("", "_blank", "width=820,height=680");
      if (!win) return;
      win.document.head.innerHTML = `<meta charset="utf-8"><title>Notlar</title><style>
        *{box-sizing:border-box;margin:0;padding:0}
        body{font-family:Arial,sans-serif;font-size:14px;line-height:1.7;color:#111827;padding:32px 40px}
        h3{font-size:15px;font-weight:700;color:#374151;margin-bottom:10px;padding-bottom:8px;border-bottom:1px solid #e5e7eb}
        .sep{margin:24px 0;border-top:1px solid #e5e7eb}
        @media print{body{padding:16px 20px}}
      </style>`;
      win.document.body.innerHTML = htmlBody;
      win.focus();
      setTimeout(() => { win.print(); }, 350);
    }
  }, [sections, extractTextFromHtml]);

  // Load
  useEffect(() => { loadNotes(); }, [loadNotes]);

  useEffect(() => {
    if (!activeSectionId && sections.length > 0) setActiveSectionId(sections[0].id);
  }, [sections, activeSectionId]);

  // Bölmə dəyişəndə
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    if (previousSectionIdRef.current && previousSectionIdRef.current !== activeSectionId) {
      const prevId = previousSectionIdRef.current;
      const cleanHtml = getEditorHtml();
      setSections((prev) => {
        const updated = prev.map((s) =>
          s.id === prevId ? { ...s, content: cleanHtml, updatedAt: Date.now() } : s
        );
        saveToLocalStorage(updated, activeSectionId);
        return updated;
      });
    }
    previousSectionIdRef.current = activeSectionId;
    const newSection = sectionsRef.current.find((s) => s.id === activeSectionId) || sectionsRef.current[0];
    editor.innerHTML = newSection?.content || "";
    editor.querySelectorAll("mark[data-search='1']").forEach((node) => {
      const parent = node.parentNode;
      if (parent) { parent.replaceChild(document.createTextNode(node.textContent || ""), node); parent.normalize(); }
    });
    setSearchMatches(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSectionId, getEditorHtml, saveToLocalStorage]);

  // beforeunload + visibilitychange
  useEffect(() => {
    const save = () => {
      if (!editorRef.current || !activeSectionIdRef.current) return;
      const cleanHtml = getEditorHtml();
      const updated = sectionsRef.current.map((s) =>
        s.id === activeSectionIdRef.current ? { ...s, content: cleanHtml, updatedAt: Date.now() } : s
      );
      try {
        localStorage.setItem(storageKey, JSON.stringify({ sections: updated, activeSectionId: activeSectionIdRef.current }));
      } catch (e) { /* ignore */ }
    };
    const onVisibility = () => { if (document.hidden) save(); };
    window.addEventListener("beforeunload", save);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("beforeunload", save);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [getEditorHtml, storageKey]);

  // UNMOUNT - X düyməsi bağlayanda komponent unmount olur, burada saxlayırıq
  useEffect(() => {
    const editor = editorRef.current;
    return () => {
      const currentId = activeSectionIdRef.current;
      const currentSections = sectionsRef.current;
      if (!editor || !currentId) return;
      const clone = editor.cloneNode(true);
      clone.querySelectorAll("mark[data-search='1']").forEach((node) => {
        const parent = node.parentNode;
        if (parent) { parent.replaceChild(document.createTextNode(node.textContent || ""), node); parent.normalize(); }
      });
      const cleanHtml = clone.innerHTML.replace(/\u200B/g, "").replace(/\u00A0/g, " ");
      const updated = currentSections.map((s) =>
        s.id === currentId ? { ...s, content: cleanHtml, updatedAt: Date.now() } : s
      );
      try {
        localStorage.setItem(storageKey, JSON.stringify({ sections: updated, activeSectionId: currentId }));
      } catch (e) { /* ignore */ }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save
  useEffect(() => {
    const interval = setInterval(() => {
      if (editorRef.current && activeSectionId) syncEditorToState();
    }, 2000);
    return () => clearInterval(interval);
  }, [activeSectionId, syncEditorToState]);

  if (!isLoaded) return <div className={styles.wrapper}>Yüklənir...</div>;

  return (
    <div className={styles.wrapper}>

      {/* ── Sol sidebar ── */}
      <aside className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <i className="bi bi-journal-text" style={{ color: "var(--color-primary)" }} />
          <span>Qeydlər</span>
        </div>

        <div className={styles.sidebarSearch}>
          <InputGroup size="sm">
            <Form.Control
              placeholder="Axtar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); searchNote(); } }}
            />
            <Button variant="outline-secondary" onClick={searchNote}>
              <i className="bi bi-search" />
            </Button>
          </InputGroup>
          {searchTerm.trim() && <div className={styles.searchInfo}>{searchMatches} nəticə tapıldı</div>}
        </div>

        <div className={styles.sectionList}>
          {sections.map((section) => (
            <div
              key={section.id}
              className={`${styles.sectionItem} ${activeSectionId === section.id ? styles.sectionItemActive : ""}`}
            >
              {editingSectionId === section.id ? (
                <Form.Control
                  size="sm"
                  autoFocus
                  value={editingSectionName}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setEditingSectionName(e.target.value)}
                  onBlur={() => commitRenameSection(section.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); commitRenameSection(section.id); }
                    if (e.key === "Escape") { e.preventDefault(); cancelRenameSection(); }
                  }}
                  className={styles.sectionNameInput}
                />
              ) : (
                <>
                  <button
                    type="button"
                    className={styles.sectionBtn}
                    onClick={(e) => { e.stopPropagation(); setActiveSectionId(section.id); }}
                    onDoubleClick={(e) => { e.stopPropagation(); startRenameSection(section); }}
                    title={section.name}
                  >
                    <i className="bi bi-file-text" style={{ flexShrink: 0 }} />
                    <span className={styles.sectionBtnText}>{section.name}</span>
                  </button>
                  <div className={styles.sectionActions}>
                    <button type="button" onClick={(e) => { e.stopPropagation(); startRenameSection(section); }} title="Adı dəyiş">
                      <i className="bi bi-pencil" />
                    </button>
                    {sections.length > 1 && (
                      <button type="button" className={styles.sectionDeleteAction} onClick={(e) => { e.stopPropagation(); e.preventDefault(); requestDeleteSection(section.id); }} title="Sil">
                        <i className="bi bi-trash" />
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        <div className={styles.sidebarFooter}>
          <Button variant="outline-primary" size="sm" onClick={addSection} className="w-100 mb-2">
            <i className="bi bi-plus-lg me-1" />Yeni bölmə
          </Button>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" size="sm" style={{ flex: 1 }} onClick={() => exportNotes("txt")}>
              <i className="bi bi-file-earmark-text me-1" />TXT
            </Button>
            <Button variant="outline-secondary" size="sm" style={{ flex: 1 }} onClick={() => exportNotes("pdf")}>
              <i className="bi bi-file-earmark-pdf me-1" />PDF
            </Button>
          </div>
        </div>
      </aside>

      {/* ── Sağ məzmun ── */}
      <div className={styles.content}>

        {/* Formatlaşdırma toolbar-ı */}
        <div className={styles.toolbar}>
          <ButtonGroup size="sm">
            <Button variant="light" onMouseDown={(e) => e.preventDefault()} onClick={() => applyCommand("bold")} title="Qalın">
              <i className="bi bi-type-bold" />
            </Button>
            <Button variant="light" onMouseDown={(e) => e.preventDefault()} onClick={() => applyCommand("italic")} title="İtalik">
              <i className="bi bi-type-italic" />
            </Button>
            <Button variant="light" onMouseDown={(e) => e.preventDefault()} onClick={() => applyCommand("underline")} title="Altı xətt">
              <i className="bi bi-type-underline" />
            </Button>
          </ButtonGroup>

          <div className={styles.toolbarDivider} />

          <Dropdown title="Şrift">
            <Dropdown.Toggle
              variant="light"
              size="sm"
              style={{ width: 128, fontSize: 12, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              onMouseDown={saveCurrentSelection}
            >
              {fontFamily}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {["Arial", "Georgia", "Times New Roman", "Courier New", "Verdana", "Comic Sans MS"].map((f) => (
                <Dropdown.Item
                  key={f}
                  active={fontFamily === f}
                  style={{ fontFamily: f, fontSize: 13 }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setFontFamily(f);
                    applyFontFamily(f);
                  }}
                >
                  {f}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          <Form.Select
            size="sm"
            value={fontSize}
            onMouseDown={saveCurrentSelection}
            onChange={(e) => { setFontSize(e.target.value); applyCommand("fontSize", e.target.value); }}
            style={{ width: 68 }}
            title="Ölçü"
          >
            <option value="1">10px</option>
            <option value="2">13px</option>
            <option value="3">16px</option>
            <option value="4">18px</option>
            <option value="5">24px</option>
            <option value="6">32px</option>
          </Form.Select>

          <div className={styles.toolbarDivider} />

          <div className="d-flex align-items-center gap-1" title="Mətn rəngi">
            <i className="bi bi-fonts" style={{ color: "#6b7280", fontSize: 13, lineHeight: 1 }} />
            <Form.Control
              type="color"
              value={textColor}
              onMouseDown={saveCurrentSelection}
              onChange={(e) => { setTextColor(e.target.value); applyCommand("foreColor", e.target.value); }}
              className={styles.colorInput}
            />
          </div>

          <div className={styles.toolbarDivider} />

          <div className={styles.highlightGroup}>
            <i className="bi bi-highlighter" style={{ color: "#6b7280", fontSize: 13 }} />
            {["#fef08a", "#86efac", "#fca5a5", "#bfdbfe", "#fbcfe8", "#fed7aa"].map((color) => (
              <button
                key={color}
                type="button"
                className={`${styles.swatchBtn} ${highlightColor === color ? styles.swatchBtnActive : ""}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { setHighlightColor(color); applyHighlight(color); }}
                style={{ backgroundColor: color }}
                title="Vurğula"
              />
            ))}
            <button
              type="button"
              className={styles.swatchErase}
              onMouseDown={(e) => e.preventDefault()}
              onClick={removeHighlight}
              title="Vurğulamanı sil"
            >
              <i className="bi bi-eraser-fill" />
            </button>
          </div>
        </div>

        {/* Editor */}
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onBeforeInput={handleEditorBeforeInput}
          onKeyDown={handleEditorKeyDown}
          onInput={handleEditorInput}
          onMouseUp={saveCurrentSelection}
          onKeyUp={saveCurrentSelection}
          className={styles.editor}
        />
      </div>

      <ConfirmModal
        show={deleteConfirmShow}
        onHide={cancelDeleteSection}
        onConfirm={confirmDeleteSection}
        title="Bölmə silinsin?"
        message="Bu bölmə silinəcək. Davam etmək istəyirsiniz?"
        confirmText="Sil"
        cancelText="Ləğv et"
        variant="danger"
      />
    </div>
  );
});

NotepadTool.defaultProps = {
  notepadId: null,
  onClose: null,
};

export default NotepadTool;